import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  HostListener,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Sale, SaleItem } from '../../../shared/interfaces';
import { ReceiptFormat } from '../../../services/sales.service';

const currencyFormatter = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

// System-wide walk-in client; it has no profile to open
const GENERIC_CLIENT_ID = '10000000-0000-0000-0000-000000000001';

@Component({
  selector: 'app-sale-detail-drawer',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './sale-detail-drawer.html',
  styleUrl: './sale-detail-drawer.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaleDetailDrawer implements OnChanges {
  @Input() sale: Sale | null = null;
  @Input() open = false;
  @Input() isDownloading = false;
  @Output() closed = new EventEmitter<void>();
  @Output() receiptRequested = new EventEmitter<{ paymentId: string; format: ReceiptFormat }>();
  @ViewChild('closeBtn') closeBtn?: ElementRef<HTMLButtonElement>;

  private returnFocusTo: HTMLElement | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['open']) return;
    if (this.open) {
      this.returnFocusTo = document.activeElement as HTMLElement | null;
      setTimeout(() => this.closeBtn?.nativeElement.focus());
    } else if (this.returnFocusTo) {
      this.returnFocusTo.focus?.();
      this.returnFocusTo = null;
    }
  }

  readonly statusMap: Record<string, { label: string; class: string }> = {
    paid: {
      label: 'Pagado',
      class:
        'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20',
    },
    pending: {
      label: 'Pendiente',
      class:
        'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20',
    },
    partially_paid: {
      label: 'Parcial',
      class:
        'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/20',
    },
    failed: {
      label: 'Fallido',
      class:
        'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20',
    },
    processing: {
      label: 'Procesando',
      class:
        'bg-gray-50 text-gray-700 ring-gray-600/20 dark:bg-gray-500/10 dark:text-gray-400 dark:ring-gray-500/20',
    },
    refunded: {
      label: 'Reembolsado',
      class:
        'bg-purple-50 text-purple-700 ring-purple-600/20 dark:bg-purple-500/10 dark:text-purple-400 dark:ring-purple-500/20',
    },
  };

  readonly methodMap: Record<string, { label: string; icon: string }> = {
    cash: { label: 'Efectivo', icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z' },
    card: { label: 'Tarjeta', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
    transfer: { label: 'Transferencia', icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4' },
    credit: { label: 'Saldo a favor', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
    client_credit: { label: 'Saldo a favor', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
    epayco_link: { label: 'Link de pago', icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1' },
    manual: { label: 'Manual', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
    other: { label: 'Otro', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  };

  close(): void {
    this.closed.emit();
  }

  onDownloadReceipt(format: ReceiptFormat = 'half-letter'): void {
    if (this.sale?.paymentId) {
      this.receiptRequested.emit({ paymentId: this.sale.paymentId, format });
    }
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscKey(): void {
    if (this.open) {
      this.close();
    }
  }

  getStatusInfo(status: string): { label: string; class: string } {
    return this.statusMap[status] || { label: status, class: this.statusMap['processing'].class };
  }

  getMethodInfo(method: string): { label: string; icon: string } {
    return this.methodMap[method] || this.methodMap['other'];
  }

  formatCurrency(value: number): string {
    return currencyFormatter.format(value ?? 0);
  }

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  get pendingAmount(): number {
    if (!this.sale) return 0;
    return Math.max(0, this.sale.amount - this.sale.paidAmount);
  }

  get hasClientProfile(): boolean {
    return !!this.sale?.clientId && this.sale.clientId !== GENERIC_CLIENT_ID;
  }

  get initials(): string {
    const name = (this.sale?.clientName || '').trim();
    if (!name) return '?';
    const parts = name.split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  }

  get itemsCount(): number {
    if (!this.sale?.items) return 0;
    return this.sale.items.reduce((sum, item) => sum + item.quantity, 0);
  }
}
